import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import api from "./api";
import { createSseParser } from "./sse";

async function fetchCustomerSummary(
  customerId,
  { refresh = false, role } = {},
  { signal } = {},
) {
  const params = {};
  if (refresh) params.refresh = true;
  if (role) params.role = role;

  const { data } = await api.get(
    `api/intelligence/customers/${customerId}/summary`,
    { params, signal },
  );
  return data;
}

function useCustomerSummary(customerId, { role } = {}) {
  const forceRefresh = useRef(false);

  const query = useQuery({
    queryKey: ["intelligenceCustomerSummary", customerId, role ?? null],
    queryFn: ({ signal }) => {
      const refresh = forceRefresh.current;
      forceRefresh.current = false;
      return fetchCustomerSummary(customerId, { refresh, role }, { signal });
    },
    enabled: !!customerId,
    staleTime: 60_000,
  });

  const refresh = () => {
    forceRefresh.current = true;
    return query.refetch();
  };

  return { ...query, refresh };
}

// Force-regenerate a customer's cached "Ontik read" and push the fresh text into the
// SummaryCard's query (they share the same key). Uses refresh=true so it regenerates
// immediately from current context rather than racing the async bus invalidation.
function useRefreshCustomerSummary() {
  const queryClient = useQueryClient();

  return (customerId, { role } = {}) => {
    if (!customerId) return Promise.resolve();
    return queryClient.fetchQuery({
      queryKey: ["intelligenceCustomerSummary", customerId, role ?? null],
      queryFn: ({ signal }) =>
        fetchCustomerSummary(customerId, { refresh: true, role }, { signal }),
      staleTime: 0,
    });
  };
}

// Composer autocomplete: live contextual prompt suggestions from the intelligence-service. An empty prefix
// asks for opening "starters"; a non-empty one asks for completions of what's being typed. The customer's
// context is resolved server-side, so the client only sends the prefix and how many it wants.
async function fetchAutocompleteSuggestions(customerId, { prefix = "", count = 3 } = {}, { signal } = {}) {
  const { data } = await api.post(
    `api/intelligence/customers/${encodeURIComponent(customerId)}/autocomplete`,
    { prefix, count },
    { signal },
  );
  return data?.suggestions ?? [];
}

// Debounced autocomplete suggestions for the composer. Keeps the previous list visible while the next one
// loads (placeholderData) so the panel doesn't flicker empty between keystrokes.
function useAutocompleteSuggestions({ customerId, prefix = "", count = 3, enabled = true }) {
  const debouncedPrefix = useDebounce(prefix, 300);

  const query = useQuery({
    queryKey: ["autocompleteSuggestions", customerId, debouncedPrefix, count],
    queryFn: ({ signal }) =>
      fetchAutocompleteSuggestions(customerId, { prefix: debouncedPrefix, count }, { signal }),
    enabled: enabled && !!customerId,
    staleTime: 30_000,
    // Keep the last set on screen while typing refines it, but never carry one customer's over to another.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === customerId ? previous : undefined,
  });

  return { suggestions: query.data ?? [], isFetching: query.isFetching };
}

// Catalogue autocomplete: the curated suggestion templates (global — cached long) and the customer's slot
// bag they're filled from. Matching happens in the browser, so neither is fetched per keystroke.
async function fetchSuggestionCatalogue(customerId, { signal } = {}) {
  const { data } = await api.get(
    `api/intelligence/customers/${encodeURIComponent(customerId)}/autocomplete/catalogue`,
    { signal },
  );
  return data || null;
}

function useSuggestionCatalogue({ customerId, enabled = true }) {
  const query = useQuery({
    queryKey: ["suggestionCatalogue"],
    queryFn: ({ signal }) => fetchSuggestionCatalogue(customerId, { signal }),
    enabled: enabled && !!customerId,
    staleTime: 10 * 60_000,
  });
  return { catalogue: query.data ?? null };
}

async function fetchSuggestionSlots(customerId, { signal } = {}) {
  const { data } = await api.get(
    `api/intelligence/customers/${encodeURIComponent(customerId)}/autocomplete/slots`,
    { signal },
  );
  return data || null;
}

// Refreshed in the background after each reply (see useSmartNoteController) so eligibility tracks work
// that's just been set up.
function useSuggestionSlots({ customerId, enabled = true }) {
  const query = useQuery({
    queryKey: ["suggestionSlots", customerId],
    queryFn: ({ signal }) => fetchSuggestionSlots(customerId, { signal }),
    enabled: enabled && !!customerId,
    staleTime: 5 * 60_000,
  });
  return { slotBag: query.data ?? null };
}

// Batches composer suggestion usage events (impressions/accepts/fills) for the catalogue's ranking and the
// catalogue-vs-model comparison. Fire-and-forget: a lost batch only costs a little ranking signal.
function useSuggestionEvents(customerId) {
  const queue = useRef([]);
  const timer = useRef(null);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    const batch = queue.current.splice(0, 200);
    if (!batch.length || !customerId) return;
    api
      .post(`api/intelligence/customers/${encodeURIComponent(customerId)}/autocomplete/events`, batch)
      .catch(() => {});
  }, [customerId]);

  // Send what's queued when switching customer or closing, under the customer it was recorded for.
  useEffect(() => flush, [flush]);

  return useCallback(
    (event) => {
      queue.current.push(event);
      if (queue.current.length >= 50) flush();
      else {
        clearTimeout(timer.current);
        timer.current = setTimeout(flush, 3000);
      }
    },
    [flush],
  );
}

// The signed-in user's past Smart Note threads about a customer, newest first (the history rail). The BFF
// stamps merchant + user, so this only ever lists the caller's own conversations.
async function fetchCustomerConversations(customerId, { signal } = {}) {
  const { data } = await api.get(
    `api/intelligence/customers/${customerId}/conversations`,
    { signal },
  );
  return data;
}

function useCustomerConversations(customerId, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["customerConversations", customerId],
    queryFn: ({ signal }) => fetchCustomerConversations(customerId, { signal }),
    enabled: !!customerId && enabled,
    staleTime: 15_000,
  });
}

// One past thread's full transcript, oldest first, to rehydrate the chat when a history entry is reopened.
async function fetchConversationTurns(customerId, conversationId, { signal } = {}) {
  const { data } = await api.get(
    `api/intelligence/customers/${customerId}/conversations/${encodeURIComponent(conversationId)}/turns`,
    { signal },
  );
  return data;
}

const CONNECTION_LOST = "The connection was lost before the reply arrived.";
const TURN_FAILED = "The assistant could not complete this turn.";

const reportStep = (data, onStep) => {
  try {
    const { label, state } = JSON.parse(data);
    onStep?.({ label, state });
  } catch {
    // A single malformed progress frame must never fail the turn.
  }
};

const readErrorMessage = (data) => {
  try {
    return JSON.parse(data).message || TURN_FAILED;
  } catch {
    return TURN_FAILED;
  }
};

const isAborted = (error) =>
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.name === "AbortError";

// A pod dying mid-turn errors the body stream instead of ending it, so that network
// failure reads as a lost connection while a caller's abort still propagates as itself.
const readChunk = async (reader) => {
  try {
    return await reader.read();
  } catch (error) {
    if (isAborted(error)) throw error;
    throw new Error(CONNECTION_LOST, { cause: error });
  }
};

async function readStreamedTurn(stream, handlers = {}) {
  const { onStep, onToken, onDraft, onAction } = handlers;
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser();

  try {
    for (;;) {
      const { value, done } = await readChunk(reader);
      const frames = done
        ? parser.flush()
        : parser.push(decoder.decode(value, { stream: true }));

      for (const frame of frames) {
        switch (frame.event) {
          case "step":
            reportStep(frame.data, onStep);
            break;
          // The reply text as it decodes. Appended to show the reply forming; the terminal `reply`
          // frame stays authoritative, so a self-assess rewrite still corrects it.
          case "token":
            onToken?.(parseFrame(frame.data)?.text ?? "");
            break;
          // The composed reply and cards, before the slower action pre-creation. The cards have no
          // action id yet — their controls stay in a preparing state until the matching `action` frame.
          case "draft":
            onDraft?.(parseFrame(frame.data));
            break;
          // One suggestion's action, pre-created and ready: merge it onto the matching card.
          case "action":
            onAction?.(parseFrame(frame.data));
            break;
          case "reply":
            return JSON.parse(frame.data);
          case "error":
            throw new Error(readErrorMessage(frame.data));
          default:
            // An unknown frame type is ignored, so the server can add frames without breaking us.
            break;
        }
      }

      if (done) throw new Error(CONNECTION_LOST);
    }
  } finally {
    reader.cancel().catch(() => {});
  }
}

function parseFrame(data) {
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

// One turn of the conversational signal flow, streamed: the BFF reports progress steps
// while the turn runs and ends with the reply payload. The thread is scoped server-side
// by (merchant, user, customer, conversationId); the merchant + user are stamped by the
// BFF, so the client only supplies the customer, thread id and message. The bearer token
// lives inside a request interceptor, so this must go through the shared instance's
// fetch adapter rather than a raw fetch().
async function converseStream(
  { customerId, conversationId, message, role, mentionedUserIds },
  { signal, onStep, onToken, onDraft, onAction } = {},
) {
  const response = await api.post(
    "api/intelligence/converse",
    { customerId, conversationId, message, role, mentionedUserIds },
    {
      signal,
      adapter: "fetch",
      responseType: "stream",
      headers: { Accept: "text/event-stream" },
    },
  );

  return readStreamedTurn(response.data, { onStep, onToken, onDraft, onAction });
}

// Preload a customer's context when the Smart Note opens for them, so the first turn's Context step reads
// it from cache instead of re-fetching (~1.4s off the critical path). Fire-and-forget and best-effort: a
// failure just means the turn fetches context itself, so errors are swallowed. The BFF stamps the merchant
// + user ids; keyed server-side by customer, so it can never prime one customer's context for another.
async function warmContext(customerId, role) {
  if (!customerId) return;
  try {
    await api.post("api/intelligence/warm-context", { customerId, role });
  } catch {
    // best-effort — the turn fetches its own context if this didn't land.
  }
}

// Decide on a suggested action (an "Action" signal's card buttons). The BFF stamps the
// merchant + user ids and relays to the intelligence-service, which approves/rejects with
// the action service under THIS user's identity and records the decision on the thread.
// `amendments` (accept only) maps a step id from the suggestion's extracted.steps to that
// step's FULL replacement payload — the user's edits, validated by the action-service
// before the approved work dispatches.
// Returns { outcome, message } — outcome "Applied" means the decision landed; anything
// else (e.g. "InvalidState" for an already-decided/expired suggestion, or
// "InvalidAmendments" for edits the action-service refused) carries a user-presentable
// message.
async function decideAction({ actionId, conversationId, customerId, accept, amendments }) {
  const { data } = await api.post(
    `api/intelligence/actions/${encodeURIComponent(actionId)}/${accept ? "accept" : "reject"}`,
    { conversationId, customerId, amendments },
  );
  return data;
}

function useDecideAction() {
  return useMutation({ mutationFn: decideAction });
}

// Confirm a cancellation suggestion (a Cancellation signal's card). The BFF stamps the merchant +
// user ids and relays to the intelligence-service, which cancels the targeted work with the action
// service under THIS user's identity and records it on the thread. This button is the ONLY thing
// that cancels — the chat reply alone never does. `scope` is the signal's extracted.scope relayed
// back verbatim ("WholeAction" or "FutureInstallments" for a plan's not-yet-started instalments).
// Returns { outcome, message } — "Applied" means it was cancelled; anything else (e.g.
// "InvalidState" for work that already finished) carries a user-presentable message.
async function cancelAction({ actionId, conversationId, customerId, scope }) {
  const { data } = await api.post(
    `api/intelligence/actions/${encodeURIComponent(actionId)}/cancel`,
    { conversationId, customerId, scope },
  );
  return data;
}

function useCancelAction() {
  return useMutation({ mutationFn: cancelAction });
}

// Accept a task suggestion, fanning it out to the final colleagues the user tagged in the card.
// The BFF validates the assignee ids against the merchant's users, recomposes the pre-created task
// action to one task per assignee, and approves it. `title`/`dueAt` carry the card's edited values.
// Returns { outcome, message } — "Applied" means the tasks were created.
async function acceptTask({ actionId, conversationId, customerId, assigneeUserIds, title, dueAt }) {
  const { data } = await api.post(
    `api/intelligence/tasks/${encodeURIComponent(actionId)}/accept`,
    { conversationId, customerId, assigneeUserIds, title, dueAt },
  );
  return data;
}

function useAcceptTask() {
  return useMutation({ mutationFn: acceptTask });
}

export {
  fetchCustomerSummary,
  useCustomerSummary,
  useRefreshCustomerSummary,
  fetchAutocompleteSuggestions,
  useAutocompleteSuggestions,
  fetchSuggestionCatalogue,
  useSuggestionCatalogue,
  fetchSuggestionSlots,
  useSuggestionSlots,
  useSuggestionEvents,
  fetchCustomerConversations,
  useCustomerConversations,
  fetchConversationTurns,
  converseStream,
  warmContext,
  decideAction,
  useDecideAction,
  cancelAction,
  useCancelAction,
  acceptTask,
  useAcceptTask,
};
